<template>
  <div class="relative overflow-hidden text-center pb-6 lg:mt-10 mt-4 px-1">
    <!-- Left Side -->
    <div class="flex flex-col gap-10 lg:gap-12 items-start lg:max-w-[45%] relative z-10">
      <!-- Congrats w/ Img -->
      <div class="flex flex-col text-left">
        <div class="relative w-[150px]">
          <img :src="successMedallion" alt="Investment Success" class="h-[96px] lg:h-36 z-10" />
          <div class="absolute top-0 right-12 lg:top-5 lg:right-5">
            <div
              class="h-10 w-10 rounded-full bg-success flex items-center justify-center border-[6px] border-bg-canvas"
            >
              <Icon name="Check" class="h-5 w-5 text-always-light" :stroke-width="3" />
            </div>
          </div>
        </div>
        <div class="mt-4 lg:mt-6">
          <!-- S1 Quiet Period heading -->
          <h3
            v-if="isS1QuietPeriod"
            class="text-2xl lg:text-[32px] text-strongest font-semibold leading-8 lg:leading-10"
          >
            Congratulations! <br />
            We'll notify you with next steps.
            <!-- {{
              completedInvestment?.offering_type === "S1"
                ? "Registered Offering"
                : completedInvestment?.offering_type || "Registered Offering"
            }} -->
          </h3>
          <!-- Default heading -->
          <h3
            v-else
            class="text-2xl lg:text-[32px] text-strongest font-semibold leading-6 lg:leading-9"
          >
            Congratulations on your <br />
            new
            {{
              completedInvestment?.offering_type === "S1"
                ? "Registered Offering (S1)"
                : completedInvestment?.offering_type || "Registered Offering (S1)"
            }}
            investment!
          </h3>
          <p class="mt-2 text-xs lg:text-base font-inter font-medium text-light">
            {{
              completedInvestment?.is_short_term
                ? `${completedInvestment.bond_term} month${completedInvestment.bond_term !== 1 ? "s" : ""} term`
                : `${completedInvestment?.bond_term ? completedInvestment.bond_term / 12 : 3} year term`
            }}
            <span class="mx-2">&bull;</span> {{ completedInvestment?.interest_rate || 9 }}% interest
            rate <span class="mx-2">&bull;</span>
            {{
              completedInvestment?.is_compounded ? "Compounded Monthly" : "Interest Paid Monthly"
            }}
          </p>
        </div>
      </div>

      <!-- What Happens Next -->
      <div class="text-left mb-2 lg:mb-0">
        <h4 class="text-base lg:text-[18px] font-semibold font-inter text-strongest mb-5">
          What happens next
        </h4>

        <div v-if="isS1QuietPeriod" class="flex flex-col">
          <!-- Step 2: Payment after signing -->
          <div class="flex items-start gap-4">
            <div class="flex flex-col items-center">
              <div
                class="h-8 w-6 rounded-full bg-tab-bg-selected flex items-center justify-center flex-shrink-0"
              >
                <Icon
                  name="Banknote"
                  class="h-4.5 w-4.5 text-tab-icon-selected"
                  :stroke-width="2"
                />
              </div>
              <div class="w-[2px] h-10 bg-tab-bg-selected"></div>
            </div>
            <div class="-mt-1">
              <p class="font-inter font-semibold text-strongest">
                {{ paymentStepLabel }} <span class="leading-[0] text-light"> *</span>
              </p>
              <p class="font-inter text-light text-sm font-medium">
                {{ paymentStepText }}
              </p>
            </div>
          </div>
          <!-- Step 1: Notify when able to sign -->
          <div class="flex items-start gap-4">
            <div class="flex flex-col items-center">
              <div
                class="h-8 w-6 rounded-full bg-tab-bg-selected flex items-center justify-center flex-shrink-0"
              >
                <Icon name="Bell" class="h-4.5 w-4.5 text-tab-icon-selected" :stroke-width="2" />
              </div>
              <div class="w-[2px] h-10 bg-tab-bg-selected"></div>
            </div>
            <div class="-mt-1">
              <p class="font-inter font-semibold text-strongest">
                We'll notify you when your documents are ready to sign
              </p>
              <p class="font-inter text-light text-sm font-medium">
                Once the registration statement is declared effective by the SEC
              </p>
            </div>
          </div>
          <!-- Step 3: Interest -->
          <div class="flex items-start gap-4">
            <div class="flex flex-col items-center">
              <div
                class="h-8 w-6 rounded-full bg-success flex items-center justify-center flex-shrink-0"
              >
                <Icon name="DollarSign" class="h-4 w-4 text-always-light" :stroke-width="3" />
              </div>
            </div>
            <div class="-mt-1">
              <p class="font-inter font-semibold text-strongest">
                1 year after your first payment <span class="leading-[0] text-light"> *</span>
              </p>
              <p class="font-inter text-strongest text-sm font-medium">
                You have gained
                {{ completedInvestment?.interest_rate || 9 }}% on your investment
              </p>
            </div>
          </div>
        </div>

        <div v-else class="flex flex-col">
          <!-- Step 1 -->
          <div class="flex items-start gap-4">
            <div class="flex flex-col items-center">
              <div
                class="h-8 w-6 rounded-full bg-tab-bg-selected flex items-center justify-center flex-shrink-0"
              >
                <Icon
                  name="Banknote"
                  class="h-4.5 w-4.5 text-tab-icon-selected"
                  :stroke-width="2"
                />
              </div>
              <div class="w-[2px] h-10 bg-tab-bg-selected"></div>
            </div>
            <div class="-mt-1">
              <p class="font-inter font-semibold text-strongest">
                In 1-2 business days <span class="leading-[0] text-light"> *</span>
              </p>
              <p class="font-inter text-light text-sm font-medium">
                <span v-if="isIRA" class="!font-inter">
                  Phoenix will contact you regarding next steps on transferring your IRA.
                </span>
                <span v-else class="!font-inter">Your payment goes through</span>
              </p>
            </div>
          </div>
          <!-- Step 2 -->
          <div class="flex items-start gap-4">
            <div class="flex flex-col items-center">
              <div
                class="h-8 w-6 rounded-full bg-tab-bg-selected flex items-center justify-center flex-shrink-0"
              >
                <Icon name="Percent" class="h-4 w-4 text-tab-icon-selected" :stroke-width="3" />
              </div>
              <div class="w-[2px] h-10 bg-tab-bg-selected"></div>
            </div>
            <div class="-mt-1">
              <p class="font-inter font-semibold text-strongest">Within 40 days</p>
              <p class="font-inter text-light text-sm font-medium">
                You get your first
                {{ completedInvestment?.is_compounded || true ? "interest" : "monthly" }} payment
              </p>
            </div>
          </div>
          <!-- Step 3 -->
          <div class="flex items-start gap-4">
            <div class="flex flex-col items-center">
              <div
                class="h-8 w-6 rounded-full bg-success flex items-center justify-center flex-shrink-0"
              >
                <Icon name="DollarSign" class="h-4 w-4 text-always-light" :stroke-width="3" />
              </div>
            </div>
            <div v-if="completedInvestment.is_short_term" class="-mt-1">
              <p class="font-inter font-semibold text-strongest">Continue or Redeem</p>
              <p class="font-inter text-light text-sm font-medium">
                You can select your preference during our defined redemption windows
              </p>
            </div>
            <div v-if="!completedInvestment.is_short_term" class="-mt-1">
              <p class="font-inter font-semibold text-strongest">
                1 year after your first payment <span class="leading-[0] text-light"> *</span>
              </p>
              <p class="font-inter text-strongest text-sm font-medium">
                You have gained
                {{ completedInvestment?.interest_rate || 9 }}% on your investment
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- CTAS -->
      <div class="flex flex-col lg:flex-row items-start gap-3">
        <Button @click="handlePushToDashboard" size="lg-md" variant="always-primary">
          <template #label>
            <p class="!font-league">Back to dashboard</p>
          </template>
        </Button>
        <Button @click="handleCallClick" size="lg-md" variant="always-paper">
          <template #label> Got Questions? 855-732-6395 </template>
        </Button>
      </div>
      <div class="text-xxs text-light font-medium shrink-0 text-left -mt-4">
        <p v-if="isS1QuietPeriod" class="font-inter">
          * Timing may vary depending on the qualification process
        </p>
        <p v-else class="font-inter">* Check payments may take longer than ACH or Wire</p>
        <p v-if="isIRA" class="font-inter">
          * Timing may vary depending on how quickly your custodian funds the investment
        </p>
        <p class="font-inter">
          * Compounding investments will have a slightly higher return after 1 year
        </p>
        <p class="font-inter">
          *
          {{
            paymentMethod === "Wire"
              ? "Allow additional time for delivery and processing."
              : "Funds are typically received within 1-2 business days."
          }}
        </p>
      </div>
    </div>
    <div
      class="hidden col-span-2 lg:block absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none select-none z-0 w-[466px] h-[558px] lg:w-[566px] lg:h-[558px]"
      :class="
        UIStore.is_dark_mode
          ? '[mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_65%,rgba(0,0,0,1)_100%)] [webkit-mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_90%,rgba(0,0,0,0)_98%)]'
          : '[mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_65%,rgba(0,0,0,0)_100%)] [webkit-mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_90%,rgba(0,0,0,0)_98%)]'
      "
      :style="{
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskPosition: 'right center',
        maskPosition: 'right center',
        opacity: UIStore.is_dark_mode ? 0.5 : 0.3,
        filter: 'sepia(0.7) saturate(3.5) hue-rotate(-10deg) brightness(0.6)',
      }"
    >
      <img :src="phxLogo" alt="" class="w-full h-full object-contain object-right" />
    </div>
    <!-- Mobile: bird pinned to bottom-right of viewport -->
    <div
      class="lg:hidden fixed bottom-0 right-0 pointer-events-none select-none z-0"
      :class="
        UIStore.is_dark_mode
          ? '[mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_65%,rgba(0,0,0,1)_100%)] [webkit-mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_90%,rgba(0,0,0,0)_98%)]'
          : '[mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_65%,rgba(0,0,0,0)_100%)] [webkit-mask-image:linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.65)_90%,rgba(0,0,0,0)_98%)]'
      "
      :style="{
        width: '75vw',
        height: '75vw',
        bottom: '-20vw',
        right: '-35vw',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        opacity: UIStore.is_dark_mode ? 0.5 : 0.3,
        filter: 'sepia(0.7) saturate(3.5) hue-rotate(-10deg) brightness(0.6)',
      }"
    >
      <img :src="phxLogo" alt="" class="w-full h-full object-contain" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  useDataStore,
  usePurchaseFlowStore,
  useAuthStore,
  useCrudStore,
  useUIStore,
} from "@/stores";
import { Button, Icon } from "@/components";
import { useAnalytics } from "@/composables/useAnalytics";
import phxLogo from "@/assets/images/phx_backdrop.png";
import successMedallion from "@/assets/images/investment_silver_circle.png";

const route = useRoute();
const router = useRouter();

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();
const authStore = useAuthStore();
const crudStore = useCrudStore();
const UIStore = useUIStore();

const { trackFlowCompleted, reinvestCompleted } = useAnalytics();

const FORCE_S1_QUIET_PERIOD = false;
const FORCE_IRA = false;

const completedInvestment = computed(() => {
  let investment = dataStore.investments.find(
    (investment) => investment.record_id === route.query?.investment_id
  );
  if (!investment.bond_term || !investment.interest_rate) {
    investment.bond_term =
      dataStore.offerings.find((off) => off.record_id === investment.offering_id)?.bond_term || 36;
    investment.interest_rate =
      dataStore.offerings.find((off) => off.record_id === investment.offering_id)?.interest_rate ||
      9;
  }
  return investment;
});

const isIRA = computed(() => {
  if (FORCE_IRA) return true;
  return completedInvestment.value?.type?.toLowerCase().includes("ira");
});

const isS1QuietPeriod = computed(() => {
  if (FORCE_S1_QUIET_PERIOD) return true;

  return (
    completedInvestment.value?.status === "Pending S1" &&
    dataStore.s1_offerings_in_quiet_period === true
  );
});

onMounted(async () => {
  const rolloverParentId = purchaseFlowStore.selected_rollover?.record_id ?? null;
  const rolloverAmount = purchaseFlowStore.current_investment?.amount ?? null;
  trackFlowCompleted();
  window.scrollTo(0, 0);
  purchaseFlowStore.$reset();
  if (rolloverParentId) {
    reinvestCompleted(rolloverParentId, rolloverAmount);
  }
  if (!route.query?.investment_id) {
    router.push("/dashboard");
  }
  if (localStorage.getItem("current_user")) {
    authStore.current_user = JSON.parse(localStorage.getItem("current_user"));
    authStore.is_verified = true;
  }

  await purchaseFlowStore.updateCompletedInvestment(completedInvestment.value);
  purchaseFlowStore.completed_investment = completedInvestment.value;

  let log = {
    step: "Thank You",
    substep: "",
    status: "Navigated",
    message: `Mounted Thank You Page for Investment ID: ${route.query?.investment_id}`,
    url: route.fullPath,
    investment_id: route.query?.investment_id || null,
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
});

const paymentMethod = computed(() => {
  return completedInvestment.value?.payment_method || "ACH";
});

const paymentStepText = computed(() => {
  const method = completedInvestment.value?.payment_method?.toLowerCase();
  if (method === "wire") {
    return "Please initiate your wire transfer using the instructions provided. ";
  } else if (method === "check") {
    return "Please mail your check using the instructions provided.";
  }
  return "Your payment will be processed within 1-2 business days.";
});

const paymentStepLabel = computed(() => {
  const method = completedInvestment.value?.payment_method?.toLowerCase();
  if (method === "wire" || method === "check") {
    return "Submit your payment";
  }
  return "Your payment is being processed";
});

function handleCallClick() {
  window.location.href = "tel:855-732-6395";
}

function handlePushToDashboard() {
  router.push("/dashboard");
}
</script>
